/**
 * Reports Service - Unified service for both web and mobile platforms
 * Handles report generation and management
 */
export class ReportsService {
    constructor(client) {
        this.client = client;
    }
    static getInstance(client) {
        if (!ReportsService.instances.has(client)) {
            ReportsService.instances.set(client, new ReportsService(client));
        }
        return ReportsService.instances.get(client);
    }
    /**
     * Set platform-specific file download handler
     * Must be called before using downloadReport
     */
    setDownloadHandler(handler) {
        this.downloadHandler = handler;
    }
    /**
     * Generate an invoice report for a work order
     */
    async generateInvoiceReport(request) {
        try {
            const response = await this.client.post('/reports/invoice', request);
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to generate invoice');
            }
            return response.data;
        }
        catch (error) {
            console.error('[ReportsService] Generate invoice report error:', error);
            throw error;
        }
    }
    /**
     * The work order's document as a PDF — the same file that is downloaded
     * and emailed. The endpoint streams application/pdf, which the API client
     * returns as a Blob.
     */
    async generateInvoicePdf(workOrderId) {
        const response = await this.client.post('/reports/invoice', { work_order_id: workOrderId, format: 'pdf' });
        if (!response.success || !(response.data instanceof Blob)) {
            throw new Error(response.error || 'Failed to generate the work order document');
        }
        return response.data;
    }
    /**
     * Generate a production report showing completed work orders with service details
     * For Excel/CSV formats, automatically triggers file download
     */
    async generateProductionReport(request) {
        try {
            const response = await this.client.post('/reports/production', request);
            if (!response.success) {
                throw new Error(response.error || 'Failed to generate production report');
            }
            // Handle Blob responses (Excel/CSV files)
            if (response.data instanceof Blob) {
                if (!this.downloadHandler) {
                    throw new Error('Download handler not set. Cannot download file.');
                }
                const ext = request.format === 'excel' ? 'xlsx' : 'csv';
                const title = request.title || 'Production_Report';
                const safeTitle = title.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 50);
                const dateStr = new Date().toISOString().split('T')[0];
                const filename = `${safeTitle}_${dateStr}.${ext}`;
                this.downloadHandler.downloadBlob(response.data, filename);
                return {
                    message: 'Production report downloaded successfully',
                };
            }
            // If data is a string, it's likely HTML
            if (typeof response.data === 'string') {
                return {
                    html: response.data,
                    message: 'Production report generated successfully',
                };
            }
            return response.data;
        }
        catch (error) {
            console.error('[ReportsService] Generate production report error:', error);
            throw error;
        }
    }
    /**
     * Generate a work performance report showing employee work for a period
     * For Excel/CSV formats, automatically triggers file download
     */
    async generateWorkPerformanceReport(request) {
        try {
            const response = await this.client.post('/reports/worker-performance', request);
            if (!response.success) {
                throw new Error(response.error || 'Failed to generate work performance report');
            }
            // Handle Blob responses (Excel/CSV files)
            if (response.data instanceof Blob) {
                if (!this.downloadHandler) {
                    throw new Error('Download handler not set. Cannot download file.');
                }
                const ext = request.format === 'excel' ? 'xlsx' : 'csv';
                const title = request.title || 'Worker_Performance_Report';
                const safeTitle = title.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 50);
                const dateStr = new Date().toISOString().split('T')[0];
                const filename = `${safeTitle}_${dateStr}.${ext}`;
                this.downloadHandler.downloadBlob(response.data, filename);
                return {
                    message: 'Payroll report downloaded successfully',
                };
            }
            // If data is a string, it's likely HTML
            if (typeof response.data === 'string') {
                return {
                    html: response.data,
                    message: 'Payroll report generated successfully',
                };
            }
            return response.data;
        }
        catch (error) {
            console.error('[ReportsService] Generate work performance report error:', error);
            throw error;
        }
    }
    /**
     * Get production report data as JSON for frontend grid display
     * Returns raw data without generating a file
     */
    async getProductionReportData(request) {
        try {
            const response = await this.client.post('/reports/production/data', request);
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to get production report data');
            }
            return response.data;
        }
        catch (error) {
            console.error('[ReportsService] Get production report data error:', error);
            throw error;
        }
    }
    /**
     * Get worker performance report data as JSON for frontend grid display
     * Returns raw data without generating a file, with sorting support
     */
    async getWorkPerformanceReportData(request) {
        try {
            const response = await this.client.post('/reports/worker-performance/data', request);
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to get worker performance report data');
            }
            return response.data;
        }
        catch (error) {
            console.error('[ReportsService] Get worker performance report data error:', error);
            throw error;
        }
    }
    /**
     * Generate a closed invoice report showing invoices closed in a date range
     * For Excel/CSV formats, automatically triggers file download
     */
    async generateClosedInvoiceReport(request) {
        try {
            const response = await this.client.post('/reports/closed-invoice', request);
            if (!response.success) {
                throw new Error(response.error || 'Failed to generate closed invoice report');
            }
            // Handle Blob responses (Excel/CSV files)
            if (response.data instanceof Blob) {
                if (!this.downloadHandler) {
                    throw new Error('Download handler not set. Cannot download file.');
                }
                const ext = request.format === 'excel' ? 'xlsx' : 'csv';
                const title = request.title || 'Closed_Invoice_Report';
                const safeTitle = title.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 50);
                const dateStr = new Date().toISOString().split('T')[0];
                const filename = `${safeTitle}_${dateStr}.${ext}`;
                this.downloadHandler.downloadBlob(response.data, filename);
                return {
                    message: 'Closed invoice report downloaded successfully',
                };
            }
            // If data is a string, it's likely HTML
            if (typeof response.data === 'string') {
                return {
                    html: response.data,
                    message: 'Closed invoice report generated successfully',
                };
            }
            return response.data;
        }
        catch (error) {
            console.error('[ReportsService] Generate closed invoice report error:', error);
            throw error;
        }
    }
    /**
     * Generate an invoice batches report scoped to the Batches tab of the
     * Closed Invoice report. Mirrors generateClosedInvoiceReport but POSTs
     * to /reports/invoice-batches and carries the batch-scoped filters
     * (payment_state, sync_state, search). For Excel/CSV formats,
     * automatically triggers file download.
     */
    async generateInvoiceBatchesReport(request) {
        try {
            const response = await this.client.post('/reports/invoice-batches', request);
            if (!response.success) {
                throw new Error(response.error || 'Failed to generate invoice batches report');
            }
            // Handle Blob responses (Excel/CSV files)
            if (response.data instanceof Blob) {
                if (!this.downloadHandler) {
                    throw new Error('Download handler not set. Cannot download file.');
                }
                const ext = request.format === 'excel' ? 'xlsx' : 'csv';
                const title = request.title || 'Invoice_Batches_Report';
                const safeTitle = title.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 50);
                const dateStr = new Date().toISOString().split('T')[0];
                const filename = `${safeTitle}_${dateStr}.${ext}`;
                this.downloadHandler.downloadBlob(response.data, filename);
                return {
                    message: 'Invoice batches report downloaded successfully',
                };
            }
            // If data is a string, it's likely HTML
            if (typeof response.data === 'string') {
                return {
                    html: response.data,
                    message: 'Invoice batches report generated successfully',
                };
            }
            return response.data;
        }
        catch (error) {
            console.error('[ReportsService] Generate invoice batches report error:', error);
            throw error;
        }
    }
    /**
     * Generate the Invoice Health (invoice lifecycle health) report. Windowed
     * (rolling days) rather than date-ranged, mirroring the in-app Health tab.
     * POSTs to /reports/invoice-lifecycle-health and, for Excel/CSV, triggers a
     * file download via the platform download handler.
     */
    async generateInvoiceLifecycleHealthReport(request) {
        try {
            const response = await this.client.post('/reports/invoice-lifecycle-health', request);
            if (!response.success) {
                throw new Error(response.error || 'Failed to generate invoice health report');
            }
            // Handle Blob responses (Excel/CSV files)
            if (response.data instanceof Blob) {
                if (!this.downloadHandler) {
                    throw new Error('Download handler not set. Cannot download file.');
                }
                const ext = request.format === 'excel' ? 'xlsx' : 'csv';
                const title = request.title || 'Invoice_Health_Report';
                const safeTitle = title.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 50);
                const dateStr = new Date().toISOString().split('T')[0];
                const filename = `${safeTitle}_${dateStr}.${ext}`;
                this.downloadHandler.downloadBlob(response.data, filename);
                return {
                    message: 'Invoice health report downloaded successfully',
                };
            }
            return response.data;
        }
        catch (error) {
            console.error('[ReportsService] Generate invoice health report error:', error);
            throw error;
        }
    }
    /**
     * Generate the PDR panels report. Export-only (CSV / Excel).
     */
    async generatePDRPanelsReport(request) {
        try {
            const response = await this.client.post('/reports/pdr-panels', request);
            if (!response.success) {
                throw new Error(response.error || 'Failed to generate PDR panels report');
            }
            if (response.data instanceof Blob) {
                if (!this.downloadHandler) {
                    throw new Error('Download handler not set. Cannot download file.');
                }
                const ext = request.format === 'excel' ? 'xlsx' : 'csv';
                const title = request.title || 'PDR_Panels_Report';
                const safeTitle = title.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 50);
                const dateStr = new Date().toISOString().split('T')[0];
                const filename = `${safeTitle}_${dateStr}.${ext}`;
                this.downloadHandler.downloadBlob(response.data, filename);
                return { message: 'PDR panels report downloaded successfully' };
            }
            return response.data;
        }
        catch (error) {
            console.error('[ReportsService] Generate PDR panels report error:', error);
            throw error;
        }
    }
    /**
     * Get closed invoice report data as JSON for frontend grid display
     * Returns raw data without generating a file, with sorting support
     */
    async getClosedInvoiceReportData(request) {
        try {
            const response = await this.client.post('/reports/closed-invoice/data', request);
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to get closed invoice report data');
            }
            return response.data;
        }
        catch (error) {
            console.error('[ReportsService] Get closed invoice report data error:', error);
            throw error;
        }
    }
    /**
     * Generate a user audit report showing audit log entries for a date range
     * For Excel/CSV formats, automatically triggers file download
     */
    async generateUserAuditReport(request) {
        try {
            const response = await this.client.post('/reports/user-audit', request);
            if (!response.success) {
                throw new Error(response.error || 'Failed to generate user audit report');
            }
            // Handle Blob responses (Excel/CSV files)
            if (response.data instanceof Blob) {
                if (!this.downloadHandler) {
                    throw new Error('Download handler not set. Cannot download file.');
                }
                const ext = request.format === 'excel' ? 'xlsx' : 'csv';
                const title = request.title || 'User_Audit_Report';
                const safeTitle = title.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 50);
                const dateStr = new Date().toISOString().split('T')[0];
                const filename = `${safeTitle}_${dateStr}.${ext}`;
                this.downloadHandler.downloadBlob(response.data, filename);
                return {
                    message: 'User audit report downloaded successfully',
                };
            }
            // If data is a string, it's likely HTML
            if (typeof response.data === 'string') {
                return {
                    html: response.data,
                    message: 'User audit report generated successfully',
                };
            }
            return response.data;
        }
        catch (error) {
            console.error('[ReportsService] Generate user audit report error:', error);
            throw error;
        }
    }
    /**
     * Get user audit report data as JSON for frontend grid display
     * Returns raw data without generating a file, with sorting support
     */
    async getUserAuditReportData(request) {
        try {
            const response = await this.client.post('/reports/user-audit/data', request);
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to get user audit report data');
            }
            return response.data;
        }
        catch (error) {
            console.error('[ReportsService] Get user audit report data error:', error);
            throw error;
        }
    }
    /**
     * Generate a time clock report showing employee clock in/out times
     * For Excel/CSV formats, automatically triggers file download
     */
    async generateTimeClockReport(request) {
        try {
            const response = await this.client.post('/reports/time-clock', request);
            if (!response.success) {
                throw new Error(response.error || 'Failed to generate time clock report');
            }
            // Handle Blob responses (Excel/CSV files)
            if (response.data instanceof Blob) {
                if (!this.downloadHandler) {
                    throw new Error('Download handler not set. Cannot download file.');
                }
                const ext = request.format === 'excel' ? 'xlsx' : 'csv';
                const title = request.title || 'Time_Clock_Report';
                const safeTitle = title.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 50);
                const dateStr = new Date().toISOString().split('T')[0];
                const filename = `${safeTitle}_${dateStr}.${ext}`;
                this.downloadHandler.downloadBlob(response.data, filename);
                return {
                    message: 'Time clock report downloaded successfully',
                };
            }
            // If data is a string, it's likely HTML
            if (typeof response.data === 'string') {
                return {
                    html: response.data,
                    message: 'Time clock report generated successfully',
                };
            }
            return response.data;
        }
        catch (error) {
            console.error('[ReportsService] Generate time clock report error:', error);
            throw error;
        }
    }
    /**
     * Get time clock report data as JSON for frontend grid display
     * Returns raw data without generating a file, with sorting support
     */
    async getTimeClockReportData(request) {
        try {
            const response = await this.client.post('/reports/time-clock/data', request);
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to get time clock report data');
            }
            return response.data;
        }
        catch (error) {
            console.error('[ReportsService] Get time clock report data error:', error);
            throw error;
        }
    }
    /**
     * Generate a deleted work orders report
     * For Excel/CSV formats, automatically triggers file download
     */
    async generateDeletedWorkOrdersReport(request) {
        try {
            const response = await this.client.post('/reports/deleted-work-orders', request);
            if (!response.success) {
                throw new Error(response.error || 'Failed to generate deleted work orders report');
            }
            // Handle Blob responses (Excel/CSV files)
            if (response.data instanceof Blob) {
                if (!this.downloadHandler) {
                    throw new Error('Download handler not set. Cannot download file.');
                }
                const ext = request.format === 'excel' ? 'xlsx' : 'csv';
                const title = request.title || 'Deleted_Work_Orders_Report';
                const safeTitle = title.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 50);
                const dateStr = new Date().toISOString().split('T')[0];
                const filename = `${safeTitle}_${dateStr}.${ext}`;
                this.downloadHandler.downloadBlob(response.data, filename);
                return {
                    message: 'Deleted work orders report downloaded successfully',
                };
            }
            return response.data;
        }
        catch (error) {
            console.error('[ReportsService] Generate deleted work orders report error:', error);
            throw error;
        }
    }
    /**
     * Get deleted work order report data as JSON for frontend grid display
     * Returns raw data without generating a file, with sorting support
     */
    async getDeletedWorkOrderReportData(request) {
        try {
            const response = await this.client.post('/reports/deleted-work-orders/data', request);
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to get deleted work order report data');
            }
            return response.data;
        }
        catch (error) {
            console.error('[ReportsService] Get deleted work order report data error:', error);
            throw error;
        }
    }
    /**
     * Get per-location Performance report data as JSON for the grid.
     * No file format — CSV export is handled client-side via the
     * reportExport utility.
     */
    async getPerformanceReportData(request) {
        try {
            const response = await this.client.post('/reports/performance/data', request);
            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to get performance report data');
            }
            return response.data;
        }
        catch (error) {
            console.error('[ReportsService] Get performance report data error:', error);
            throw error;
        }
    }
}
ReportsService.instances = new Map();
/**
 * Create reports service instance (Singleton per API client)
 * Must be called with platform-specific API client
 */
export function createReportsService(apiClient) {
    return ReportsService.getInstance(apiClient);
}
